import { ref, watch } from 'vue'
import { createAudioAnalyser, RoomEvent, Track } from 'livekit-client'

import type { Ref } from 'vue'
import type { Room } from 'livekit-client'

import { refRoom } from '@hooks/classroom/room-live-kit.hook'
import { useTracksLiveKit } from '@hooks/classroom/tracks-live-kit.hook'
// helpers (sdk)

interface MicLevelReturn {
  level: Ref<number>
  start: () => void
  stop: () => void
}

export const useMicLevelLiveKit = (): MicLevelReturn => {
  const { isMicEnabled } = useTracksLiveKit()

  const level = ref<number>(0)

  let rafId = 0
  let cleanupAnalyser: (() => void) | null = null
  let stopWatch: (() => void) | null = null
  let boundRoom: Room | null = null

  const teardownAnalyser = (): void => {
    if (rafId) cancelAnimationFrame(rafId)
    rafId = 0
    cleanupAnalyser?.()
    cleanupAnalyser = null
    level.value = 0
  }

  const setupAnalyser = (): void => {
    teardownAnalyser()

    const track = refRoom.value?.localParticipant.getTrackPublication(Track.Source.Microphone)?.audioTrack
    if (!track) return

    const { calculateVolume, cleanup } = createAudioAnalyser(track)
    cleanupAnalyser = () => { void cleanup() }

    const tick = (): void => {
      level.value = calculateVolume()
      rafId = requestAnimationFrame(tick)
    }
    tick()
  }

  const onActiveDeviceChanged = (kind: MediaDeviceKind): void => {
    if (kind === 'audioinput') setupAnalyser()
  }

  const rebindAndSetup = (): void => {
    const room = refRoom.value
    if (room !== boundRoom) {
      boundRoom?.off(RoomEvent.ActiveDeviceChanged, onActiveDeviceChanged)
      room?.on(RoomEvent.ActiveDeviceChanged, onActiveDeviceChanged)
      boundRoom = room
    }
    setupAnalyser()
  }

  const start = (): void => {
    stopWatch = watch([refRoom, isMicEnabled], rebindAndSetup, { immediate: true })
  }

  const stop = (): void => {
    stopWatch?.()
    stopWatch = null
    boundRoom?.off(RoomEvent.ActiveDeviceChanged, onActiveDeviceChanged)
    boundRoom = null
    teardownAnalyser()
  }

  return { level, start, stop }
}
